import _SessionStartTryNextQuery from "../__generated__/SessionStartTryNextQuery.graphql";
import _SessionStartFavouriteQuery from "../__generated__/SessionStartFavouriteQuery.graphql";
import _SessionStartQuery from "../__generated__/SessionStartQuery.graphql";
import { AudioLines, BedDouble, BriefcaseBusiness, Coffee, MapPin, ShoppingBag, Stethoscope, Backpack, Briefcase, CalendarDays, CircleHelp, Gamepad2, GraduationCap, Eye, Heart, MessagesSquare, MoonStar, Music, Palette, Split, PawPrint, Plane, Presentation, Puzzle, Rocket, Search, Tv, UtensilsCrossed, Volleyball, Users, type LucideIcon } from 'lucide-react';
import { Suspense, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { CharacterFace, usePhase } from '../avatar/CharacterFace';
import { MeQuery } from '../components/guards';
import { QuietBoundary } from '../components/QuietBoundary';
import { registerFor, type Register } from '../profile/register';
import { favouriteTopic } from '../scrapbook/favourite.ts';
import { useSession } from '../session/context';
import { t } from '../i18n/t';
import { ADULT_SCENES, KID_GAMES, type Topic } from './topics.english.ts';
import { LessonTodayCard } from './LessonTodayCard';
import { NewMoment } from './Moments';
import { orderGames, sceneGoal, topicIcon, topicLabel, topicsFor, type TopicIcon } from './topics.ts';
import { tryNext } from './tryNext.ts';
import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';
import type { SessionStartFavouriteQuery } from '../__generated__/SessionStartFavouriteQuery.graphql';
import type { SessionStartQuery } from '../__generated__/SessionStartQuery.graphql';
import type { SessionStartTryNextQuery } from '../__generated__/SessionStartTryNextQuery.graphql';

// Its own query rather than a wider `guardsMeQuery`: every guard and the theme
// attribute read that one on every navigation, and this screen is the only
// reader of these fields (first-impressions design §6).
const Tutor = (_SessionStartQuery.hash && _SessionStartQuery.hash !== "81e8135d2c18b1fa68e1aeccd1096391" && console.error("The definition of 'SessionStartQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionStartQuery);

// Exhaustive over `TopicIcon`, so a new topic without an icon fails `tsc`.
const ICONS: Record<TopicIcon, LucideIcon> = {
  food: UtensilsCrossed,
  travel: Plane,
  work: Briefcase,
  family: Users,
  weekend: CalendarDays,
  presentation: Presentation,
  animals: PawPrint,
  toys: Puzzle,
  school: Backpack,
  sports: Volleyball,
  colours: Palette,
  space: Rocket,
  games: Gamepad2,
  cartoons: Tv,
  music: Music,
  riddles: CircleHelp,
  guess: Search,
  teach: GraduationCap,
  story: MoonStar,
  spy: Eye,
  rather: Split,
  rhyme: AudioLines,
  cafe: Coffee,
  doctor: Stethoscope,
  interview: BriefcaseBusiness,
  hotel: BedDouble,
  shop: ShoppingBag,
  directions: MapPin
};

// Kid only, and its own query so an adult's start screen reads no scrapbook.
// `first: 10` is the window the favourite counts (kid play design §2).
const Favourite = (_SessionStartFavouriteQuery.hash && _SessionStartFavouriteQuery.hash !== "efa63aa26b268ff47fda31cb8caac116" && console.error("The definition of 'SessionStartFavouriteQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionStartFavouriteQuery);

/** The heart (§2): decoration, so a slow or failed read shows a plain tile. */
function FavouriteMark({
  topic,
  tiles
}: {
  topic: string;
  tiles: readonly string[];
}) {
  const data = useLazyLoadQuery<SessionStartFavouriteQuery>(Favourite, {});
  if (favouriteTopic(data.me?.learner?.scrapbook ?? [], tiles) !== topic) return null;
  return <span className="text-primary flex items-center gap-1 text-xs font-medium">
      <Heart aria-hidden className="size-3.5 fill-current" />
      {t('session-start-favourite')}
    </span>;
}
const TryNextQuery = (_SessionStartTryNextQuery.hash && _SessionStartTryNextQuery.hash !== "94ac7876b08253432b6141208f49dda7" && console.error("The definition of 'SessionStartTryNextQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionStartTryNextQuery);

/**
 * Try this next (kid engagement PRD F3): one thing the child has not done
 * lately, named with its picture and opened in one tap. Decoration, like the
 * heart: a slow or failed read shows no card. No count, no "you haven't".
 */
function TryNextCard({
  disabled,
  onPick
}: {
  disabled: boolean;
  onPick: (topic: string) => void;
}) {
  const data = useLazyLoadQuery<SessionStartTryNextQuery>(TryNextQuery, {});
  const learner = data.me?.learner;
  const books = learner?.storyBooks ?? [];
  const next = tryNext(books, learner?.familyWeek?.talks ?? [], orderGames(KID_GAMES, new Date().getHours()), new Date());
  if (!next) return null;
  const body = 'card lift rise min-h-(--hit-min) flex-row items-center gap-4 border-2 border-primary bg-base-100 p-4 text-start';
  const heading = <span className="text-base-content/60 text-sm">{t('try-next-title')}</span>;
  if (next.kind === 'book') {
    const book = books.find(b => b.id === next.id);
    if (!book) return null;
    return <Link to={`/books/${encodeURIComponent(book.id)}`} className={body}>
        <img src={book.coverImage} alt="" aria-hidden className="h-20 w-16 shrink-0 rounded-field object-cover" />
        <span className="flex flex-col gap-1">
          {heading}
          <span lang="en" className="text-lg font-semibold">
            {book.title}
          </span>
        </span>
      </Link>;
  }
  const Icon = ICONS[topicIcon(next.topic as Topic)];
  return <button type="button" className={body} disabled={disabled} onClick={() => onPick(next.topic)}>
      <span className="tint grid size-16 shrink-0 place-items-center rounded-field">
        <Icon aria-hidden className="size-8" />
      </span>
      <span className="flex flex-col gap-1">
        {heading}
        <span className="text-lg font-semibold">{topicLabel(next.topic)}</span>
      </span>
    </button>;
}
const TEACH = KID_GAMES[2];

/**
 * "Teach Cappy" (kid play design §7.5). Reads the greeting's own query, so
 * the two share one request; until it answers the tile asks to teach "me".
 */
function NamedLabel({
  topic
}: {
  topic: string;
}) {
  const data = useLazyLoadQuery<SessionStartQuery>(Tutor, {});
  return <>{topicLabel(topic, data.me?.learner?.defaultCharacter?.displayName)}</>;
}
function TopicTile({
  topic,
  index,
  disabled,
  onPick,
  children,
  className = ''
}: {
  topic: Topic;
  index: number;
  disabled: boolean;
  onPick: (topic: string) => void;
  children?: React.ReactNode;
  className?: string;
}) {
  const Icon = ICONS[topicIcon(topic)];
  return <button className={`card lift rise min-h-(--hit-min) items-center gap-2 border border-base-300 bg-base-100 p-4 text-center disabled:pointer-events-none disabled:opacity-50 ${className}`} style={{
    '--rise-delay': `${Math.min(index, 8) * 40}ms`
  } as React.CSSProperties} disabled={disabled} onClick={() => onPick(topic)}>
      <span className="tint grid size-12 place-items-center rounded-field">
        <Icon aria-hidden className="size-6" />
      </span>
      <span className="font-medium">
        {topic === TEACH ? <QuietBoundary fallback={topicLabel(topic)}>
            <Suspense fallback={topicLabel(topic)}>
              <NamedLabel topic={topic} />
            </Suspense>
          </QuietBoundary> : topicLabel(topic)}
      </span>
      {children}
    </button>;
}

/** Today's heading: what shows while the tutor loads, or if it cannot. */
function PlainHeading({
  register
}: {
  register: Register;
}) {
  return <h1 className="text-2xl font-bold">{t(register === 'kid' ? 'session-start-kid-title' : 'session-start-title')}</h1>;
}
function TutorGreeting({
  register
}: {
  register: Register;
}) {
  const data = useLazyLoadQuery<SessionStartQuery>(Tutor, {});
  const phase = usePhase();
  const tutor = data.me?.learner?.defaultCharacter;
  if (!tutor) return <PlainHeading register={register} />;
  return <div className="flex items-end gap-3" style={{
    '--tutor-accent': tutor.accentHex
  } as React.CSSProperties}>
      <div className="rise w-20 shrink-0 sm:w-24">
        <CharacterFace artKey={tutor.artKey} accentHex={tutor.accentHex} intensity={tutor.intensity} expression="neutral" phase={phase} />
        <p className="text-center text-sm font-medium">{tutor.displayName}</p>
      </div>
      {/* The bubble is the heading, so the screen still has exactly one. */}
      <h1 className="tint rise rounded-box mb-6 px-4 py-3 text-xl font-bold [border-bottom-left-radius:0]">
        {t(register === 'kid' ? 'session-start-bubble-kid' : 'session-start-bubble')}
      </h1>
    </div>;
}

/**
 * The screen a learner sees when no conversation is running.
 *
 * Its own route element rather than an early return inside `SessionView`,
 * because the two now have different URLs: this is `/session`, a conversation
 * is `/session/:sessionId`. The session itself is started here but *owned* by
 * `SessionProvider` above, so the audio machine `startSession` brings up
 * survives the navigation to the conversation route.
 *
 * The register comes from the learner's default character — there is no session
 * character yet, because there is no session.
 */
export function SessionStart() {
  const navigate = useNavigate();
  const {
    start
  } = useSession();
  const me = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const register = registerFor(me.me?.learner?.defaultCharacter?.audience);
  const topics = topicsFor(register);
  const [starting, setStarting] = useState(false);
  const [why, setWhy] = useState<string | null>(null);
  const begin = useCallback((topic?: string) => {
    // `startSession` writes a row and mints a single-use token; a second tap
    // while the first is in flight leaves an orphaned active session behind and
    // navigates to whichever answered last.
    if (starting) return;
    setStarting(true);
    setWhy(null);
    void start(topic).then(result => {
      // Global ids are `URL_SAFE_NO_PAD` base64 (`tutor-core/src/ids.rs`),
      // so they go into a path segment unescaped.
      //
      // `replace`, because `/session` re-decides on every visit: a Back from
      // the conversation onto the start screen would find the session it
      // just left still active and bounce straight forward again.
      if ('sessionId' in result) void navigate(`/session/${result.sessionId}`, {
        replace: true
      });else {
        setWhy(result.why);
        setStarting(false);
      }
    });
  }, [start, navigate, starting]);

  // No gutters or max-width of its own: `AppLayout`'s `<main>` supplies both
  // to every route, and a second copy here indented this screen twice as far
  // as its neighbours on a phone.
  //
  // From `lg` two panes: the tutor and the start button held still on the
  // start side, the tiles beside them, so a child's whole screen is above the
  // fold (large-desktop plan, Task 3). Below `lg` each pane is `contents`, its
  // children items of the one column. The lesson card is in the tile pane,
  // after the topics, so the DOM is the drawn order at every width: an
  // `order` that moved it back on a phone left a screen reader and Tab
  // meeting it before the topics drawn above it.
  return <div className="flex flex-col gap-6 py-8 lg:grid lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start lg:gap-10">
      <div className="contents lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-6">
        <QuietBoundary>
          <Suspense fallback={null}>
            <NewMoment register={register} />
          </Suspense>
        </QuietBoundary>
        {/* Decoration: a slow or failed read leaves today's heading and working buttons (§7). */}
        <QuietBoundary fallback={<PlainHeading register={register} />}>
          <Suspense fallback={<PlainHeading register={register} />}>
            <TutorGreeting register={register} />
          </Suspense>
        </QuietBoundary>
        <button className="btn btn-primary btn-lg min-h-(--hit-min)" disabled={starting} onClick={() => begin()}>
          <MessagesSquare aria-hidden className="size-5" />
          {/* Not the heading's words again: the heading names the screen and
            the button names the action, and "Start a conversation" twice in
            a row reads as a rendering bug. */}
          {starting ? t('common-starting') : t(register === 'kid' ? 'session-start-kid' : 'session-start')}
        </button>
        {why && <div role="alert" className="alert alert-error">
            <span>{why}</span>
          </div>}
        {register === 'kid' && <QuietBoundary>
            <Suspense fallback={null}>
              <TryNextCard disabled={starting} onPick={begin} />
            </Suspense>
          </QuietBoundary>}
      </div>
      <div className="contents lg:flex lg:flex-col lg:gap-6">
        <div className="flex flex-col gap-2">
          <p className="text-base-content/60 text-sm">{t('session-start-topic')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
            {topics.map((topic, i) => <TopicTile key={topic} topic={topic} index={i} disabled={starting} onPick={begin}>
                {register === 'kid' && <QuietBoundary>
                    <Suspense fallback={null}>
                      <FavouriteMark topic={topic} tiles={topics} />
                    </Suspense>
                  </QuietBoundary>}
              </TopicTile>)}
          </div>
        </div>
        {/* This week's lesson set (lesson library PRD §4.2): renders nothing for a
          kid or when no set is started. */}
        <QuietBoundary>
          <Suspense fallback={null}>
            <LessonTodayCard />
          </Suspense>
        </QuietBoundary>
        {/* Adult role-play design §2: adult only, each tile with its goal under it. */}
        {register === 'adult' && <div className="flex flex-col gap-2">
            <p className="text-base-content/60 text-sm">{t('session-start-role-play')}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {ADULT_SCENES.map((scene, i) => <TopicTile key={scene} topic={scene} index={topics.length + i} disabled={starting} onPick={begin}>
                  <span className="text-base-content/60 text-xs">{sceneGoal(scene)}</span>
                </TopicTile>)}
            </div>
          </div>}
        {/* The games corner (kid play design §3): kid only, adults see today's screen. */}
        {register === 'kid' && <div className="flex flex-col gap-2">
            <p className="text-base-content/60 text-sm">{t('session-start-play')}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {/* An odd count in two columns leaves the last game alone under
                half a row; it spans the row instead until three columns fit
                (kid play design §8.8). Story time first in the evening (kid
                engagement PRD, F1). */}
              {orderGames(KID_GAMES, new Date().getHours()).map((game, i) => <TopicTile key={game} topic={game} index={topics.length + i} disabled={starting} onPick={begin} className={i === KID_GAMES.length - 1 ? 'col-span-2 sm:col-span-1' : ''} />)}
            </div>
          </div>}
      </div>
    </div>;
}